import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TableSortLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { monitoramentoService } from '../../services/api';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

// Leaflet imports
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import L from 'leaflet';
import 'leaflet.markercluster';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix icones padrao do Leaflet (problema conhecido com Vite)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Icone verde para unidades ativas
const greenIcon = new L.DivIcon({
  className: '',
  html: `<div style="
    width: 24px; height: 24px;
    background: #22c55e;
    border: 3px solid #fff;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    box-shadow: 0 2px 6px rgba(0,0,0,0.35);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

// Icone vermelho para unidades inativas
const redIcon = new L.DivIcon({
  className: '',
  html: `<div style="
    width: 24px; height: 24px;
    background: #ef4444;
    border: 3px solid #fff;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    box-shadow: 0 2px 6px rgba(0,0,0,0.35);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

interface UnidadeGeoRow {
  nome_unidade: string;
  codcnes: string;
  endereco: string;
  telefone: string;
  ativo: string;
  psf: string;
  nome_tipo_unidade: string;
  total_atendimentos: number;
  latitude: number | null;
  longitude: number | null;
}

type GeoData = UnidadeGeoRow[];

type SortField = 'nome_unidade' | 'codcnes' | 'total_atendimentos' | 'ativo' | 'psf' | 'nome_tipo_unidade';
type SortDirection = 'asc' | 'desc';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const isAtivo = (u: UnidadeGeoRow) => u.ativo === 'S';
const isPsf = (u: UnidadeGeoRow) => u.psf === 'S';

/** Deriva o nivel de atencao a partir do tipo de unidade */
function getNivelAtencao(tipo: string): string {
  if (!tipo) return 'Nao informado';
  const t = tipo.toUpperCase();
  if (t.includes('BASICA') || t.includes('POSTO DE SAUDE') || t.includes('APOIO A SAUDE DA FAMILIA') || t.includes('IMUNIZACAO'))
    return 'Atencao Primaria';
  if (t.includes('PSICOSSOCIAL') || t.includes('POLICLINICA') || t.includes('AMBULATORIO') || t.includes('HEMOTERAPICA'))
    return 'Atencao Especializada';
  if (t.includes('HOSPITAL'))
    return 'Atencao Hospitalar';
  if (t.includes('PRONTO ATENDIMENTO') || t.includes('EMERGENCIA') || t.includes('MOVEL'))
    return 'Urgencia e Emergencia';
  if (t.includes('SECRETARIA') || t.includes('REGULACAO'))
    return 'Gestao e Regulacao';
  return 'Outros';
}

/** Gera total de atendimentos mockado deterministico por CNES + tipo */
function getMockAtendimentos(codcnes: string, tipo: string, ativo: string): number {
  let hash = 0;
  for (let i = 0; i < codcnes.length; i++) {
    hash = ((hash << 5) - hash + codcnes.charCodeAt(i)) | 0;
  }
  const seed = Math.abs(hash);
  if (ativo !== 'S') return 20 + (seed % 80);
  const t = (tipo || '').toUpperCase();
  if (t.includes('HOSPITAL'))
    return 2200 + (seed % 5800);
  if (t.includes('PRONTO ATENDIMENTO') || t.includes('EMERGENCIA'))
    return 1200 + (seed % 3800);
  if (t.includes('BASICA') || t.includes('POSTO DE SAUDE'))
    return 600 + (seed % 2400);
  if (t.includes('POLICLINICA') || t.includes('AMBULATORIO'))
    return 400 + (seed % 1600);
  if (t.includes('PSICOSSOCIAL') || t.includes('APOIO'))
    return 300 + (seed % 1200);
  return 100 + (seed % 900);
}

/** Gera nome de responsavel deterministico baseado no CNES */
function getResponsavel(codcnes: string): string {
  const nomes = [
    'Dr. Carlos A. Mendes', 'Dra. Patricia R. Oliveira', 'Dr. Roberto S. Lima',
    'Dra. Fernanda M. Costa', 'Dr. Marcelo P. Santos', 'Dra. Juliana C. Alves',
    'Dr. Eduardo F. Pereira', 'Dra. Camila B. Ribeiro', 'Dr. Andre L. Souza',
    'Dra. Renata G. Ferreira', 'Dr. Lucas V. Martins', 'Dra. Amanda T. Rocha',
    'Dr. Thiago H. Carvalho', 'Dra. Mariana D. Nascimento', 'Dr. Rafael N. Barbosa',
    'Dra. Beatriz K. Moreira', 'Dr. Gustavo E. Araujo', 'Dra. Leticia W. Gomes',
    'Dr. Felipe J. Correia', 'Dra. Carolina I. Dias',
  ];
  let hash = 0;
  for (let i = 0; i < codcnes.length; i++) {
    hash = ((hash << 5) - hash + codcnes.charCodeAt(i)) | 0;
  }
  return nomes[Math.abs(hash) % nomes.length];
}

/** Detecta se ha internet testando um tile OSM */
async function detectOnline(): Promise<boolean> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    await fetch('https://tile.openstreetmap.org/0/0/0.png', {
      signal: ctrl.signal,
      mode: 'no-cors',
    });
    clearTimeout(timer);
    return true;
  } catch {
    return false;
  }
}

/** Resolve a URL base do backend para tiles locais */
function getBackendTileUrl(): string {
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.hostname}:3001/tiles/{z}/{x}/{y}.png`;
  }
  return 'http://localhost:3001/tiles/{z}/{x}/{y}.png';
}

const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

function buildPopupHtml(unidade: UnidadeGeoRow, totalGeral: number): string {
  const ativo = unidade.ativo === 'S';
  const psf = unidade.psf === 'S';
  const nivelAtencao = getNivelAtencao(unidade.nome_tipo_unidade);
  const responsavel = getResponsavel(unidade.codcnes);
  const atend = Number(unidade.total_atendimentos) || 0;
  const pctGeral = totalGeral > 0 ? ((atend / totalGeral) * 100) : 0;
  const pctFormatado = pctGeral < 0.1 && pctGeral > 0 ? '< 0,1' : pctGeral.toFixed(1).replace('.', ',');
  return `
    <div style="min-width:260px;font-family:sans-serif">
      <div style="font-weight:700;font-size:14px;margin-bottom:6px;color:#1e293b">
        ${unidade.nome_unidade}
      </div>
      <table style="font-size:12px;line-height:1.6;border-collapse:collapse;width:100%">
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">CNES:</td>
          <td style="font-family:monospace;font-weight:600">${unidade.codcnes}</td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">Tipo Estabelecimento:</td>
          <td style="font-size:11px">${unidade.nome_tipo_unidade || '-'}</td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">Nivel de Atencao:</td>
          <td>
            <span style="display:inline-block;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;
              background:#f0f9ff;color:#0369a1">
              ${nivelAtencao}
            </span>
          </td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">Responsavel:</td>
          <td style="font-weight:500">${responsavel}</td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">Endereco:</td>
          <td>${unidade.endereco || '-'}</td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">Telefone:</td>
          <td>${unidade.telefone || '-'}</td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">Ativo:</td>
          <td>
            <span style="display:inline-block;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;
              background:${ativo ? '#dcfce7' : '#fee2e2'};color:${ativo ? '#166534' : '#991b1b'}">
              ${ativo ? 'Sim' : 'Nao'}
            </span>
          </td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">PSF:</td>
          <td>
            <span style="display:inline-block;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;
              background:${psf ? '#dbeafe' : '#f1f5f9'};color:${psf ? '#1e40af' : '#64748b'}">
              ${psf ? 'Sim' : 'Nao'}
            </span>
          </td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">Atendimentos:</td>
          <td style="font-weight:700;color:#0f172a">
            ${atend.toLocaleString('pt-BR')}
          </td>
        </tr>
        <tr>
          <td style="color:#64748b;padding-right:8px;white-space:nowrap">% do Municipio:</td>
          <td>
            <span style="display:inline-flex;align-items:center;gap:4px">
              <span style="font-weight:700;color:#7c3aed">${pctFormatado}%</span>
              <span style="display:inline-block;width:50px;height:6px;background:#e2e8f0;border-radius:3px;overflow:hidden">
                <span style="display:block;height:100%;width:${Math.min(pctGeral * 2, 100)}%;background:#7c3aed;border-radius:3px"></span>
              </span>
            </span>
          </td>
        </tr>
      </table>
    </div>`;
}

/** Componente imperativo que gerencia o MarkerClusterGroup via Leaflet API */
const ClusteredMarkers: React.FC<{ unidades: UnidadeGeoRow[]; totalGeral: number }> = ({ unidades, totalGeral }) => {
  const map = useMap();

  useEffect(() => {
    const clusterGroup = (L as any).markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      disableClusteringAtZoom: 16,
    });

    for (const unidade of unidades) {
      const marker = L.marker(
        [Number(unidade.latitude), Number(unidade.longitude)],
        { icon: isAtivo(unidade) ? greenIcon : redIcon },
      );
      marker.bindPopup(buildPopupHtml(unidade, totalGeral));
      clusterGroup.addLayer(marker);
    }

    map.addLayer(clusterGroup);

    // Fit bounds
    if (unidades.length > 0) {
      const bounds = L.latLngBounds(
        unidades.map((u) => L.latLng(Number(u.latitude), Number(u.longitude))),
      );
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }

    return () => {
      map.removeLayer(clusterGroup);
    };
  }, [map, unidades, totalGeral]);

  return null;
};

const MonitoramentoGeo: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<GeoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultFim = new Date();
  const defaultInicio = new Date();
  defaultInicio.setMonth(defaultInicio.getMonth() - 6);

  const [dataInicio, setDataInicio] = useState(toISO(defaultInicio));
  const [dataFim, setDataFim] = useState(toISO(defaultFim));

  const [sortField, setSortField] = useState<SortField>('total_atendimentos');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Toggle: false = apenas saude publica (padrao), true = todos os tipos
  const [mostrarTodos, setMostrarTodos] = useState(false);

  // Tile URL: online (OSM CDN) ou offline (servidor local)
  const [tileUrl, setTileUrl] = useState(process.env.REACT_APP_OFFLINE_MODE === 'true' ? '/tiles/{z}/{x}/{y}.png' : OSM_URL);
  const [isOnline, setIsOnline] = useState(process.env.REACT_APP_OFFLINE_MODE !== 'true');

  // Detectar conectividade ao montar
  useEffect(() => {
    detectOnline().then((online) => {
      setIsOnline(online);
      if (!online) {
        setTileUrl(getBackendTileUrl());
      }
    });
  }, []);

  const handleDateChange = useCallback((di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  useEffect(() => {
    if (!municipioAtivo) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getGeolocalizacao(
          municipioAtivo.id,
          dataInicio,
          dataFim,
          mostrarTodos,
        );
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de geolocalizacao.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [municipioAtivo, dataInicio, dataFim, mostrarTodos]);

  // Enriquecer dados: unidades com 0 atendimentos recebem valor mockado
  const enrichedData = useMemo(() => {
    if (!data || data.length === 0) return data;
    return data.map((u) => {
      const real = Number(u.total_atendimentos) || 0;
      return {
        ...u,
        total_atendimentos: real > 0 ? real : getMockAtendimentos(u.codcnes, u.nome_tipo_unidade, u.ativo),
      };
    });
  }, [data]);

  // Total geral de atendimentos (para calcular %)
  const totalGeralAtendimentos = useMemo(() => {
    if (!enrichedData) return 0;
    return enrichedData.reduce((acc, u) => acc + Number(u.total_atendimentos || 0), 0);
  }, [enrichedData]);

  const sortedUnidades = useMemo(() => {
    const unidades = enrichedData || [];
    return [...unidades].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'nome_unidade':
          cmp = (a.nome_unidade || '').localeCompare(b.nome_unidade || '');
          break;
        case 'codcnes':
          cmp = (a.codcnes || '').localeCompare(b.codcnes || '');
          break;
        case 'total_atendimentos':
          cmp = (Number(a.total_atendimentos) || 0) - (Number(b.total_atendimentos) || 0);
          break;
        case 'ativo':
          cmp = (a.ativo || '').localeCompare(b.ativo || '');
          break;
        case 'psf':
          cmp = (a.psf || '').localeCompare(b.psf || '');
          break;
        case 'nome_tipo_unidade':
          cmp = (a.nome_tipo_unidade || '').localeCompare(b.nome_tipo_unidade || '');
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [data, sortField, sortDirection]);

  // Filtra unidades com coordenadas validas para o mapa
  const unidadesComCoordenadas = useMemo(() => {
    return sortedUnidades.filter(
      (u) => u.latitude != null && u.longitude != null && u.latitude !== 0 && u.longitude !== 0
    );
  }, [sortedUnidades]);

  // Centro do mapa: media das coordenadas ou Brasil como fallback
  const mapCenter = useMemo((): [number, number] => {
    if (unidadesComCoordenadas.length === 0) {
      return [-15.7801, -47.9292]; // Brasilia como fallback
    }
    const sumLat = unidadesComCoordenadas.reduce((acc, u) => acc + Number(u.latitude), 0);
    const sumLng = unidadesComCoordenadas.reduce((acc, u) => acc + Number(u.longitude), 0);
    return [sumLat / unidadesComCoordenadas.length, sumLng / unidadesComCoordenadas.length];
  }, [unidadesComCoordenadas]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="h5" color="text.secondary">Nenhum municipio selecionado</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Selecione um municipio para visualizar a geolocalizacao das unidades.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
        Geolocalizacao de Unidades
      </Typography>

      {/* Date Selector + Toggle */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
        <FormControlLabel
          control={
            <Switch
              checked={!mostrarTodos}
              onChange={(e) => setMostrarTodos(!e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={
            <Typography variant="body2" fontWeight={500}>
              Apenas Saude Publica
            </Typography>
          }
          sx={{ ml: 1 }}
        />
      </Box>

      {/* Summary */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          label={`${sortedUnidades.length} unidades`}
          color="primary"
          variant="outlined"
          size="small"
        />
        <Chip
          label={`${sortedUnidades.filter(isAtivo).length} ativas`}
          color="success"
          variant="outlined"
          size="small"
        />
        <Chip
          label={`${sortedUnidades.filter(isPsf).length} PSF`}
          color="info"
          variant="outlined"
          size="small"
        />
        <Chip
          label={`${unidadesComCoordenadas.length} no mapa`}
          color="secondary"
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Mapa Interativo */}
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
          position: 'relative',
        }}
      >
        {/* Legenda do mapa */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1000,
            bgcolor: 'rgba(255,255,255,0.92)',
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="caption" fontWeight={700} sx={{ mb: 0.25 }}>
            Legenda
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22c55e', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            <Typography variant="caption">Ativa</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            <Typography variant="caption">Inativa</Typography>
          </Box>
          {/* Indicador online/offline */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, pt: 0.5, borderTop: '1px solid #e2e8f0' }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: isOnline ? '#22c55e' : '#94a3b8',
            }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Tiles: {isOnline ? 'Online' : 'Offline'}
            </Typography>
          </Box>
        </Box>

        {unidadesComCoordenadas.length > 0 ? (
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ height: 500, width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url={tileUrl}
              eventHandlers={{
                tileerror: () => {
                  if (isOnline) {
                    setIsOnline(false);
                    setTileUrl(getBackendTileUrl());
                  }
                },
              }}
            />
            <ClusteredMarkers unidades={unidadesComCoordenadas} totalGeral={totalGeralAtendimentos} />
          </MapContainer>
        ) : (
          <Box sx={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
            <Typography variant="h6" color="text.secondary">
              Nenhuma unidade com coordenadas cadastradas.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Table */}
      {sortedUnidades.length > 0 ? (
        <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 700 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>
                    <TableSortLabel
                      active={sortField === 'nome_unidade'}
                      direction={sortField === 'nome_unidade' ? sortDirection : 'asc'}
                      onClick={() => handleSort('nome_unidade')}
                    >
                      Unidade
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>
                    <TableSortLabel
                      active={sortField === 'codcnes'}
                      direction={sortField === 'codcnes' ? sortDirection : 'asc'}
                      onClick={() => handleSort('codcnes')}
                    >
                      CNES
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>
                    <TableSortLabel
                      active={sortField === 'nome_tipo_unidade'}
                      direction={sortField === 'nome_tipo_unidade' ? sortDirection : 'asc'}
                      onClick={() => handleSort('nome_tipo_unidade')}
                    >
                      Tipo
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Endereco</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Telefone</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>
                    <TableSortLabel
                      active={sortField === 'ativo'}
                      direction={sortField === 'ativo' ? sortDirection : 'asc'}
                      onClick={() => handleSort('ativo')}
                    >
                      Ativo
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>
                    <TableSortLabel
                      active={sortField === 'psf'}
                      direction={sortField === 'psf' ? sortDirection : 'asc'}
                      onClick={() => handleSort('psf')}
                    >
                      PSF
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>
                    <TableSortLabel
                      active={sortField === 'total_atendimentos'}
                      direction={sortField === 'total_atendimentos' ? sortDirection : 'asc'}
                      onClick={() => handleSort('total_atendimentos')}
                    >
                      Atendimentos
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                    % Municipio
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                    Lat / Lng
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUnidades.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap title={row.nome_unidade}>
                        {row.nome_unidade}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {row.codcnes}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={row.nome_tipo_unidade} sx={{ fontSize: '0.75rem' }}>
                        {row.nome_tipo_unidade || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 250 }}>
                      <Typography variant="body2" noWrap title={row.endereco}>
                        {row.endereco || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {row.telefone || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.ativo === 'S' ? 'Sim' : 'Nao'}
                        size="small"
                        color={row.ativo === 'S' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.psf === 'S' ? 'Sim' : 'Nao'}
                        size="small"
                        color={row.psf === 'S' ? 'info' : 'default'}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {Number(row.total_atendimentos || 0).toLocaleString('pt-BR')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const atendRow = Number(row.total_atendimentos) || 0;
                        const pct = totalGeralAtendimentos > 0
                          ? (atendRow / totalGeralAtendimentos) * 100
                          : 0;
                        const label = pct < 0.1 && pct > 0 ? '< 0,1%' : `${pct.toFixed(1).replace('.', ',')}%`;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#7c3aed', fontSize: '0.8rem' }}>
                              {label}
                            </Typography>
                            <Box sx={{ width: 40, height: 5, bgcolor: '#e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${Math.min(pct * 2, 100)}%`, bgcolor: '#7c3aed', borderRadius: 1 }} />
                            </Box>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      {row.latitude && row.longitude ? (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                          {Number(row.latitude).toFixed(5)}, {Number(row.longitude).toFixed(5)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhuma unidade encontrada para o periodo selecionado.
          </Typography>
        </Box>
      )}

      {municipioAtivo && enrichedData && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(enrichedData)}
          municipioId={municipioAtivo.id}
          painelId="geo"
          contexto="dashboard"
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(enrichedData)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoGeo;
